// frontend/src/features/meetings/MeetingsView.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import MeetingsHeader from './components/MeetingsHeader';
import MeetingsKpiStrip from './components/MeetingsKpiStrip';
import MeetingCalendarView from './components/MeetingCalendarView';
import MeetingCard from './components/MeetingCard';
import MeetingDetailModal from './components/MeetingDetailModal';
import ScheduleMeetingModal, { getRoleTierLevel } from './components/ScheduleMeetingModal';
import ScheduleGroupMeetingModal from './components/ScheduleGroupMeetingModal';
import MeetingSettingsModal from './components/MeetingSettingsModal';
import { useAuth } from '../../contexts/KeycloakContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import meetingsApi, {
  isMeetingPast,
  getEffectiveMeetingStatus,
  canJoinMeeting,
} from './api/meetingsApi';
import './MeetingsView.css';

export default function MeetingsView() {
  const { user } = useAuth();
  const {
    workspaceContext,
    isOwner,
    workspaceId,
    workspaceRole,
    displayRole,
    isOrganizationAdmin,
    isSalesManager,
    isSalesMember,
    isResearcher,
    isViewer,
    ownerEmail: ctxOwnerEmail,
  } = useWorkspace();

  // Determine logged in member and token context
  const effectiveUser = useMemo(() => {
    if (user && user.email) return user;
    try {
      const storedToken = localStorage.getItem('token') || localStorage.getItem('access_token');
      if (storedToken) {
        const parts = storedToken.split('.');
        if (parts.length === 3) {
          const tokenData = JSON.parse(atob(parts[1]));
          if (tokenData) {
            return {
              id: tokenData.sub,
              email: tokenData.email,
              username: tokenData.preferred_username,
              name:
                tokenData.name ||
                [tokenData.given_name, tokenData.family_name].filter(Boolean).join(' ') ||
                tokenData.preferred_username,
            };
          }
        }
      }
    } catch (_) {}
    return user || null;
  }, [user]);

  const effectiveWorkspaceId = useMemo(() => {
    return (
      workspaceId ||
      workspaceContext?.primaryWorkspace?.workspaceId ||
      workspaceContext?.workspaceId ||
      workspaceContext?.workspaces?.[0]?.workspaceId ||
      workspaceContext?.id ||
      workspaceContext?.workspace_id ||
      null
    );
  }, [workspaceId, workspaceContext]);

  const currentUserEmail = useMemo(() => {
    return (
      user?.email ||
      effectiveUser?.email ||
      (isOwner ? ctxOwnerEmail || workspaceContext?.ownerEmail : '') ||
      ''
    );
  }, [user, effectiveUser, isOwner, ctxOwnerEmail, workspaceContext]);

  const currentUserId = useMemo(() => {
    return user?.id || effectiveUser?.id || '';
  }, [user, effectiveUser]);

  const ownerEmail = useMemo(() => {
    return (
      ctxOwnerEmail ||
      workspaceContext?.ownerEmail ||
      (isOwner ? user?.email || effectiveUser?.email : null) ||
      user?.email ||
      effectiveUser?.email ||
      ''
    );
  }, [ctxOwnerEmail, workspaceContext, isOwner, user, effectiveUser]);

  const ownerName = useMemo(() => {
    return user?.username || effectiveUser?.username || workspaceContext?.username || '';
  }, [user, effectiveUser, workspaceContext]);

  const [meetings, setMeetings] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [availabilitySettings, setAvailabilitySettings] = useState({
    timezone: 'Asia/Singapore (UTC+8)',
    workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    workingHoursStart: '09:00',
    workingHoursEnd: '18:00',
    bufferBeforeMinutes: 15,
    bufferAfterMinutes: 15,
    minNoticeHours: 4,
    maxAdvanceDays: 14,
    connectedCalendars: [],
    routingRule: 'round_robin',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Load meetings, meeting types, availability, and hosts from Database
  const loadDatabaseData = useCallback(async () => {
    if (!effectiveWorkspaceId) return;
    try {
      setIsLoading(true);

      const urlParams = new URLSearchParams(window.location.search);
      const connectedProvider = urlParams.get('calendar_connected'); // 'google' | 'microsoft'
      const oAuthAccount = urlParams.get('account') || currentUserEmail || ownerEmail;

      if (connectedProvider === 'google' || connectedProvider === 'microsoft') {
        const buildUpdatedCals = (prevList) => {
          const list =
            prevList && prevList.length > 0
              ? prevList
              : [
                  {
                    provider: 'google',
                    name: 'Google Calendar',
                    account: null,
                    status: 'disconnected',
                    syncTarget: 'primary',
                    availableCalendars: [],
                    conflictCalendars: ['primary'],
                    conferencingDefault: 'google_meet',
                    lastSyncAt: null,
                    health: null,
                  },
                  {
                    provider: 'microsoft',
                    name: 'Microsoft 365 Outlook',
                    account: null,
                    status: 'disconnected',
                    syncTarget: 'default',
                    availableCalendars: [],
                    conflictCalendars: ['default'],
                    conferencingDefault: 'teams',
                    lastSyncAt: null,
                    health: null,
                  },
                ];
          return list.map((c) =>
            c.provider === connectedProvider
              ? {
                  ...c,
                  status: 'connected',
                  account: oAuthAccount,
                  availableCalendars: [
                    {
                      id: c.syncTarget || (c.provider === 'google' ? 'primary' : 'default'),
                      name: `Primary (${oAuthAccount})`,
                      isPrimary: true,
                    },
                  ],
                  lastSyncAt: new Date().toISOString(),
                  health: {
                    status: 'healthy',
                    latencyMs: 120,
                    tokenValid: true,
                    webhookActive: true,
                  },
                }
              : {
                  ...c,
                  status: 'disconnected',
                  account: null,
                  availableCalendars: [],
                  conflictCalendars: [],
                  lastSyncAt: null,
                  health: null,
                }
          );
        };

        setAvailabilitySettings((prev) => {
          const updated = buildUpdatedCals(prev?.connectedCalendars);
          meetingsApi
            .updateAvailabilitySettings(effectiveWorkspaceId, {
              connectedCalendars: updated,
              hostEmail: currentUserEmail,
              userId: currentUserId,
            })
            .catch((e) =>
              console.warn('Could not persist calendar connection on OAuth return:', e?.message)
            );
          return {
            ...prev,
            connectedCalendars: updated,
          };
        });

        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const [dbMeetings, dbTypes, dbSettings, dbHosts] = await Promise.all([
        meetingsApi.getMeetings(effectiveWorkspaceId),
        meetingsApi.getMeetingTypes(effectiveWorkspaceId),
        meetingsApi.getAvailabilitySettings(
          effectiveWorkspaceId,
          currentUserEmail || ownerEmail,
          currentUserId
        ),
        meetingsApi.getWorkspaceHosts(effectiveWorkspaceId),
      ]);

      if (Array.isArray(dbMeetings)) {
        setMeetings(dbMeetings);
      }
      if (Array.isArray(dbTypes) && dbTypes.length > 0) {
        setMeetingTypes(dbTypes);
      }
      if (dbSettings) {
        if (connectedProvider) {
          // Keep only the verified connected calendar; disconnect all other providers
          const cals = (dbSettings.connectedCalendars || []).map((c) =>
            c.provider === connectedProvider
              ? {
                  ...c,
                  status: 'connected',
                  account: oAuthAccount || c.account,
                  lastSyncAt: new Date().toISOString(),
                }
              : {
                  ...c,
                  status: 'disconnected',
                  account: null,
                  availableCalendars: [],
                  conflictCalendars: [],
                  lastSyncAt: null,
                  health: null,
                }
          );
          setAvailabilitySettings({
            ...dbSettings,
            connectedCalendars: cals,
          });
        } else {
          // Even when not returning from OAuth, enforce mutual exclusivity on dbSettings
          const connectedCals = (dbSettings.connectedCalendars || []).filter(
            (c) => c.status === 'connected'
          );
          if (connectedCals.length > 1) {
            // Keep only the most recently synced provider
            const active = [...connectedCals].sort(
              (a, b) =>
                new Date(b.lastSyncAt || 0).getTime() - new Date(a.lastSyncAt || 0).getTime()
            )[0].provider;
            const normalizedCals = dbSettings.connectedCalendars.map((c) =>
              c.provider === active
                ? c
                : {
                    ...c,
                    status: 'disconnected',
                    account: null,
                    availableCalendars: [],
                    conflictCalendars: [],
                    lastSyncAt: null,
                    health: null,
                  }
            );
            setAvailabilitySettings({
              ...dbSettings,
              connectedCalendars: normalizedCals,
            });
          } else {
            setAvailabilitySettings(dbSettings);
          }
        }
      }
      if (Array.isArray(dbHosts)) {
        setHosts(dbHosts);
      }
    } catch (err) {
      console.warn('Could not load meetings database records:', err?.message);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveWorkspaceId, currentUserEmail, currentUserId, ownerEmail, ownerName]);

  useEffect(() => {
    loadDatabaseData();
  }, [loadDatabaseData]);

  const currentMember = useMemo(() => {
    const usr = effectiveUser || user;
    return (hosts || []).find(
      (h) =>
        (usr?.id && (String(h.id) === String(usr.id) || String(h.userId) === String(usr.id))) ||
        (usr?.email && h.email && h.email.toLowerCase() === usr.email.toLowerCase()) ||
        (usr?.username && h.username && h.username.toLowerCase() === usr.username.toLowerCase()) ||
        (usr?.name && h.name && h.name.toLowerCase() === usr.name.toLowerCase())
    );
  }, [hosts, effectiveUser, user]);

  const rawUserRole = useMemo(() => {
    if (isOwner || workspaceContext?.isOwner) return 'owner';
    if (isOrganizationAdmin || workspaceContext?.isOrganizationAdmin) return 'admin';
    if (isSalesManager || workspaceContext?.isSalesManager) return 'manager';
    if (isViewer || workspaceContext?.isViewer) return 'viewer';
    if (isResearcher || workspaceContext?.isResearcher) return 'researcher';
    if (isSalesMember || workspaceContext?.isSalesMember) return 'member';

    return (
      currentMember?.rawRole ||
      currentMember?.role ||
      currentMember?.workspace_role ||
      workspaceRole ||
      workspaceContext?.workspaceRole ||
      workspaceContext?.workspace_role ||
      workspaceContext?.displayRole ||
      workspaceContext?.role ||
      displayRole ||
      user?.workspaceRole ||
      user?.workspace_role ||
      user?.role ||
      ''
    );
  }, [
    isOwner,
    isOrganizationAdmin,
    isSalesManager,
    isViewer,
    isResearcher,
    isSalesMember,
    currentMember,
    workspaceRole,
    workspaceContext,
    displayRole,
    user,
  ]);

  const roleTierLevel = useMemo(() => {
    if (isOwner || workspaceContext?.isOwner) return 4;
    if (isOrganizationAdmin || workspaceContext?.isOrganizationAdmin) return 3;
    if (isSalesManager || workspaceContext?.isSalesManager) return 2;
    if (isViewer || workspaceContext?.isViewer) return 0;
    return getRoleTierLevel(rawUserRole);
  }, [isOwner, isOrganizationAdmin, isSalesManager, isViewer, workspaceContext, rawUserRole]);

  // Level 4 (Owner), Level 3 (Admin), Level 2 (Manager) can create meetings and see team meetings
  // Level 1 (Member, Viewer, Researcher) CANNOT create meetings and only view their own meetings
  const canCreateMeeting = roleTierLevel >= 2;
  const isSelfOnlyView = roleTierLevel < 2;

  // Scoped meetings list:
  // - Owner (Level 4): full visibility across workspace
  // - Manager / Admin (Level 2-3): sees team meetings & manager-created meetings, but CANNOT see Owner's private unassigned meetings
  // - Member / Viewer / Researcher (Level 1): strictly self-only
  const scopedMeetings = useMemo(() => {
    if (!Array.isArray(meetings)) return [];

    // Level 4 (Owner): Full visibility into everything
    if (roleTierLevel >= 4) {
      return meetings;
    }

    const usr = effectiveUser || user;
    const usrId = usr?.id ? String(usr.id) : '';
    const usrEmail = usr?.email ? usr.email.toLowerCase() : '';
    const usrName = usr?.name
      ? usr.name.toLowerCase()
      : usr?.fullName
        ? usr.fullName.toLowerCase()
        : '';
    const usrUsername = usr?.username ? usr.username.toLowerCase() : '';
    const memberName = currentMember?.name ? currentMember.name.toLowerCase() : '';
    const memberId = currentMember?.id ? String(currentMember.id) : '';

    // Level 3 (Admin):
    // Sees Manager/Member/Viewer/Researcher meetings and Admin unassigned, but CANNOT see Owner's private unassigned meetings
    if (roleTierLevel === 3) {
      const ownerHost = (hosts || []).find((h) => {
        const r = String(h.role || h.rawRole || '').toLowerCase();
        return r.includes('owner');
      });
      const ownerId = ownerHost?.id ? String(ownerHost.id) : '';
      const ownerEmailStr = (ownerHost?.email || ownerEmail || '').toLowerCase();

      return meetings.filter((m) => {
        const hId = m.hostId ? String(m.hostId) : '';
        const hEmail = m.hostEmail ? m.hostEmail.toLowerCase() : '';
        const hName = m.hostName ? m.hostName.toLowerCase() : '';
        const brief = m.preMeetingBrief || {};
        const isUnassigned =
          hName === 'unassigned' ||
          brief.isUnassigned === true ||
          m.isUnassigned === true ||
          hId === 'unassigned';

        if (isUnassigned) {
          const isMyCreatedMeeting =
            (usrId && (hId === usrId || String(brief.creatorId) === usrId)) ||
            (usrEmail &&
              (hEmail === usrEmail || String(brief.creatorEmail).toLowerCase() === usrEmail));
          if (isMyCreatedMeeting) return true;

          // If scheduled by Owner, Admin cannot see it
          const isOwnerMeeting =
            (ownerId && (hId === ownerId || String(brief.creatorId) === ownerId)) ||
            (ownerEmailStr &&
              (hEmail === ownerEmailStr ||
                String(brief.creatorEmail).toLowerCase() === ownerEmailStr)) ||
            brief.creatorRole === 'owner' ||
            brief.creatorRole === 'org_owner';

          if (isOwnerMeeting) return false;

          return true;
        }

        return true;
      });
    }

    // Level 2 (Sales Manager):
    // Sees Member/Viewer/Researcher meetings and Manager unassigned, but CANNOT see Owner's or Admin's unassigned meetings
    if (roleTierLevel === 2) {
      const ownerOrAdminHosts = (hosts || []).filter((h) => {
        const r = String(h.role || h.rawRole || '').toLowerCase();
        return (
          r.includes('owner') || r.includes('admin') || r.includes('director') || r.includes('head')
        );
      });

      return meetings.filter((m) => {
        const hId = m.hostId ? String(m.hostId) : '';
        const hEmail = m.hostEmail ? m.hostEmail.toLowerCase() : '';
        const hName = m.hostName ? m.hostName.toLowerCase() : '';
        const brief = m.preMeetingBrief || {};
        const isUnassigned =
          hName === 'unassigned' ||
          brief.isUnassigned === true ||
          m.isUnassigned === true ||
          hId === 'unassigned';

        if (isUnassigned) {
          // If scheduled/owned by current manager, they can see it
          const isMyCreatedMeeting =
            (usrId && (hId === usrId || String(brief.creatorId) === usrId)) ||
            (usrEmail &&
              (hEmail === usrEmail || String(brief.creatorEmail).toLowerCase() === usrEmail));
          if (isMyCreatedMeeting) return true;

          // If scheduled by Owner or Admin, Manager cannot see it
          const isOwnerOrAdminMeeting =
            ownerOrAdminHosts.some(
              (o) =>
                (o.id && (hId === String(o.id) || String(brief.creatorId) === String(o.id))) ||
                (o.email &&
                  (hEmail === o.email.toLowerCase() ||
                    String(brief.creatorEmail).toLowerCase() === o.email.toLowerCase()))
            ) || ['owner', 'org_owner', 'admin', 'org_admin'].includes(brief.creatorRole);

          if (isOwnerOrAdminMeeting) return false;

          return true;
        }

        // Assigned meeting: Manager can see team meetings
        return true;
      });
    }

    // Level 0 & 1 (Member, Viewer, Researcher): Strictly self-only
    return meetings.filter((m) => {
      const hId = m.hostId ? String(m.hostId).trim() : '';
      const hEmail = m.hostEmail ? String(m.hostEmail).trim().toLowerCase() : '';
      const hName = m.hostName ? String(m.hostName).trim().toLowerCase() : '';
      const brief = m.preMeetingBrief || {};
      const assignedId = brief.assignedToId ? String(brief.assignedToId).trim() : '';
      const assignedEmail = brief.assignedToEmail
        ? String(brief.assignedToEmail).trim().toLowerCase()
        : '';
      const assignedName = brief.assignedToName
        ? String(brief.assignedToName).trim().toLowerCase()
        : '';

      // Never show unassigned meetings created by others to members
      const isUnassigned =
        hName === 'unassigned' ||
        brief.isUnassigned === true ||
        m.isUnassigned === true ||
        hId === 'unassigned';
      if (isUnassigned) return false;

      const matchId =
        (usrId &&
          (hId === usrId || hId === memberId || assignedId === usrId || assignedId === memberId)) ||
        (memberId && (hId === memberId || assignedId === memberId));

      const matchEmail =
        (usrEmail &&
          (hEmail === usrEmail ||
            hEmail.includes(usrEmail) ||
            usrEmail.includes(hEmail) ||
            assignedEmail === usrEmail)) ||
        (currentMember?.email &&
          (hEmail === String(currentMember.email).trim().toLowerCase() ||
            hEmail.includes(String(currentMember.email).trim().toLowerCase()) ||
            String(currentMember.email).trim().toLowerCase().includes(hEmail) ||
            assignedEmail === String(currentMember.email).trim().toLowerCase()));

      const matchName =
        (usrName &&
          (hName === usrName ||
            hName.includes(usrName) ||
            usrName.includes(hName) ||
            assignedName === usrName)) ||
        (usrUsername &&
          (hName === usrUsername ||
            hName.includes(usrUsername) ||
            usrUsername.includes(hName) ||
            assignedName === usrUsername)) ||
        (memberName &&
          (hName === memberName ||
            hName.includes(memberName) ||
            memberName.includes(hName) ||
            assignedName === memberName)) ||
        (currentMember?.name &&
          (hName === String(currentMember.name).trim().toLowerCase() ||
            hName.includes(String(currentMember.name).trim().toLowerCase()) ||
            String(currentMember.name).trim().toLowerCase().includes(hName) ||
            assignedName === String(currentMember.name).trim().toLowerCase())) ||
        (currentMember?.username &&
          (hName === String(currentMember.username).trim().toLowerCase() ||
            hName.includes(String(currentMember.username).trim().toLowerCase()) ||
            String(currentMember.username).trim().toLowerCase().includes(hName) ||
            assignedName === String(currentMember.username).trim().toLowerCase()));

      return Boolean(matchId || matchEmail || matchName || (!usrId && !usrEmail && !usrName));
    });
  }, [meetings, roleTierLevel, effectiveUser, user, currentMember, hosts, ownerEmail]);

  const [activeFilterTab, setActiveFilterTab] = useState('all'); // 'all' | 'upcoming' | 'needs_recap' | 'completed' | 'cancelled'
  const [searchQuery, setSearchQuery] = useState('');

  const [detailMeeting, setDetailMeeting] = useState(null);
  const [detailModalTab, setDetailModalTab] = useState('info');
  const [rescheduleMeeting, setRescheduleMeeting] = useState(null);
  const [rescheduleLockedMeeting, setRescheduleLockedMeeting] = useState(null);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isGroupScheduleOpen, setIsGroupScheduleOpen] = useState(false);
  const [scheduleDefaultDate, setScheduleDefaultDate] = useState(null);
  const [scheduleLead, setScheduleLead] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.openSchedule) {
      setIsScheduleOpen(true);
      if (location.state?.lead) {
        setScheduleLead(location.state.lead);
      }
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (_) {}
    }
  }, [location.state]);

  // Filtered meetings list
  const filteredMeetings = useMemo(() => {
    return scopedMeetings.filter((m) => {
      const isPast = isMeetingPast(m);
      const effectiveStatus = getEffectiveMeetingStatus(m);

      // Tab filter
      if (
        activeFilterTab === 'upcoming' &&
        ((effectiveStatus !== 'confirmed' && effectiveStatus !== 'in_progress') || isPast)
      )
        return false;
      if (activeFilterTab === 'needs_recap' && effectiveStatus !== 'needs_recap') return false;
      if (activeFilterTab === 'completed' && effectiveStatus !== 'completed') return false;
      if (activeFilterTab === 'cancelled' && effectiveStatus !== 'cancelled') return false;

      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = m.title?.toLowerCase().includes(q);
        const matchGuest = m.guestName?.toLowerCase().includes(q);
        const matchCompany = m.guestCompany?.toLowerCase().includes(q);
        const matchHost = m.hostName?.toLowerCase().includes(q);
        if (!matchTitle && !matchGuest && !matchCompany && !matchHost) return false;
      }

      return true;
    });
  }, [scopedMeetings, activeFilterTab, searchQuery]);

  // Modal open handlers
  const handleOpenDetail = (meeting, tab = 'info') => {
    setDetailMeeting(meeting);
    setDetailModalTab(tab);
  };

  const handleSaveSettings = async (newSettings) => {
    setAvailabilitySettings(newSettings);
    setIsSettingsOpen(false);

    if (effectiveWorkspaceId) {
      try {
        const updated = await meetingsApi.updateAvailabilitySettings(effectiveWorkspaceId, {
          ...newSettings,
          hostEmail: currentUserEmail || ownerEmail,
          userId: currentUserId,
        });
        if (updated) {
          setAvailabilitySettings(updated);
        }
      } catch (err) {
        console.error('Failed to save availability settings to database:', err);
      }
    }
  };

  const handleScheduleNewMeeting = async (newMeeting) => {
    // Optimistic UI update
    setMeetings((prev) => [newMeeting, ...prev]);

    if (effectiveWorkspaceId) {
      try {
        const saved = await meetingsApi.createMeeting(effectiveWorkspaceId, newMeeting);
        if (saved && saved.id) {
          setMeetings((prev) => prev.map((m) => (m.id === newMeeting.id ? saved : m)));
        }
      } catch (err) {
        console.error('Failed to persist new meeting to database:', err);
        setMeetings((prev) => prev.filter((m) => m.id !== newMeeting.id));
        alert(
          err.response?.data?.message ||
            err.message ||
            'Failed to schedule meeting. Please verify calendar connection.'
        );
      }
    }
  };

  const handleSaveRecap = async (meetingId, recapData) => {
    // Optimistic UI update
    setMeetings((prev) =>
      prev.map((m) =>
        m.id === meetingId
          ? {
              ...m,
              status: 'completed',
              postMeetingRecap: recapData,
            }
          : m
      )
    );
    if (detailMeeting && detailMeeting.id === meetingId) {
      setDetailMeeting((prev) => ({
        ...prev,
        status: 'completed',
        postMeetingRecap: recapData,
      }));
    }

    if (effectiveWorkspaceId) {
      try {
        await meetingsApi.saveMeetingRecap(effectiveWorkspaceId, meetingId, recapData);
      } catch (err) {
        console.error('Failed to save meeting recap to database:', err);
      }
    }
  };

  const handleCancelMeeting = async (meetingId, reason) => {
    // Optimistic UI update
    setMeetings((prev) =>
      prev.map((m) =>
        m.id === meetingId
          ? {
              ...m,
              status: 'cancelled',
              cancellationReason: reason,
            }
          : m
      )
    );
    if (detailMeeting && detailMeeting.id === meetingId) {
      setDetailMeeting((prev) => ({
        ...prev,
        status: 'cancelled',
        cancellationReason: reason,
      }));
    }

    if (effectiveWorkspaceId) {
      try {
        await meetingsApi.updateMeeting(effectiveWorkspaceId, meetingId, {
          status: 'cancelled',
          cancellationReason: reason,
        });
      } catch (err) {
        console.error('Failed to update meeting cancellation to database:', err);
      }
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    // Optimistic UI update
    setMeetings((prev) => prev.filter((m) => m.id !== meetingId));
    if (detailMeeting && detailMeeting.id === meetingId) {
      setDetailMeeting(null);
    }

    if (effectiveWorkspaceId) {
      try {
        await meetingsApi.deleteMeeting(effectiveWorkspaceId, meetingId);
      } catch (err) {
        console.error('Failed to delete meeting from database:', err);
      }
    }
  };

  const handleReschedule = (meeting) => {
    if (!meeting) return;
    if (canJoinMeeting(meeting) || getEffectiveMeetingStatus(meeting) === 'in_progress') {
      setRescheduleLockedMeeting(meeting);
      return;
    }
    setDetailMeeting(null);
    setRescheduleMeeting(meeting);
  };

  const handleSaveReschedule = async (updatedMeeting) => {
    // Optimistic UI update
    setMeetings((prev) =>
      prev.map((m) =>
        m.id === updatedMeeting.id
          ? {
              ...m,
              ...updatedMeeting,
              status: 'confirmed',
              cancellationReason: null,
            }
          : m
      )
    );
    setRescheduleMeeting(null);

    if (effectiveWorkspaceId) {
      try {
        await meetingsApi.updateMeeting(effectiveWorkspaceId, updatedMeeting.id, {
          title: updatedMeeting.title,
          startTime: updatedMeeting.startTime,
          endTime: updatedMeeting.endTime,
          originalStartTime: updatedMeeting.originalStartTime,
          originalEndTime: updatedMeeting.originalEndTime,
          durationMinutes: updatedMeeting.durationMinutes,
          hostId: updatedMeeting.hostId,
          hostName: updatedMeeting.hostName,
          hostEmail: updatedMeeting.hostEmail,
          status: 'confirmed',
          cancellationReason: null,
          guestName: updatedMeeting.guestName,
          guestEmail: updatedMeeting.guestEmail,
          guestCompany: updatedMeeting.guestCompany,
          guestTitle: updatedMeeting.guestTitle,
          guestNotes: updatedMeeting.guestNotes,
          conferencingProvider: updatedMeeting.conferencingProvider,
          meetingTypeId: updatedMeeting.meetingTypeId,
          leadScore: updatedMeeting.leadScore,
          dealEstimate: updatedMeeting.dealEstimate,
          currency: updatedMeeting.currency,
        });
      } catch (err) {
        console.error('Failed to update rescheduled meeting in database:', err);
      }
    }
  };

  // Status counts for toolbar pills
  const statusCounts = useMemo(
    () => ({
      all: scopedMeetings.length,
      upcoming: scopedMeetings.filter(
        (m) =>
          (getEffectiveMeetingStatus(m) === 'confirmed' ||
            getEffectiveMeetingStatus(m) === 'in_progress') &&
          !isMeetingPast(m)
      ).length,
      needs_recap: scopedMeetings.filter((m) => getEffectiveMeetingStatus(m) === 'needs_recap')
        .length,
      completed: scopedMeetings.filter((m) => getEffectiveMeetingStatus(m) === 'completed').length,
      cancelled: scopedMeetings.filter((m) => getEffectiveMeetingStatus(m) === 'cancelled').length,
    }),
    [scopedMeetings]
  );

  const isCalendarConnected = useMemo(() => {
    if (!availabilitySettings?.connectedCalendars) return false;
    return availabilitySettings.connectedCalendars.some(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
  }, [availabilitySettings]);
  const isGoogleCalendarConnected = isCalendarConnected;

  return (
    <div className="meetings-page-container">
      {/* Top Header & Navigation */}
      <MeetingsHeader
        activeFilterTab={activeFilterTab}
        onFilterTabChange={setActiveFilterTab}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusCounts={statusCounts}
        isCalendarConnected={isCalendarConnected}
        isGoogleCalendarConnected={isGoogleCalendarConnected}
        onOpenScheduleModal={() => {
          if (!canCreateMeeting) return;
          setIsScheduleOpen(true);
        }}
        onOpenGroupScheduleModal={() => {
          if (!canCreateMeeting) return;
          setIsGroupScheduleOpen(true);
        }}
        onOpenSettingsModal={() => setIsSettingsOpen(true)}
        canCreateMeeting={canCreateMeeting}
      />

      {/* KPI Summary Cards */}
      <MeetingsKpiStrip meetings={scopedMeetings} />

      {/* Main Content: Calendar Dashboard View */}
      <div className="meetings-content-area">
        <MeetingCalendarView
          meetings={filteredMeetings}
          workspaceId={effectiveWorkspaceId}
          currentUser={effectiveUser || user}
          currentMember={currentMember}
          ownerEmail={ownerEmail}
          availabilitySettings={availabilitySettings}
          onSelectMeeting={(m) => handleOpenDetail(m, 'info')}
          onOpenScheduleModal={(prefilledDate) => {
            if (!canCreateMeeting) return;
            if (!isCalendarConnected) {
              setIsSettingsOpen(true);
              return;
            }
            setScheduleDefaultDate(prefilledDate);
            setIsScheduleOpen(true);
          }}
          hosts={hosts}
          meetingTypes={meetingTypes}
          canCreateMeeting={canCreateMeeting}
          roleTierLevel={roleTierLevel}
        />
      </div>

      {/* Modals */}
      {detailMeeting && (
        <MeetingDetailModal
          meeting={detailMeeting}
          initialTab={detailModalTab}
          hosts={hosts}
          currentUser={user}
          currentUserRole={rawUserRole}
          isOwner={isOwner}
          onClose={() => setDetailMeeting(null)}
          onSaveRecap={handleSaveRecap}
          onReschedule={handleReschedule}
          onCancelMeeting={handleCancelMeeting}
          onDeleteMeeting={handleDeleteMeeting}
        />
      )}

      {rescheduleMeeting && (
        <ScheduleMeetingModal
          initialMeeting={rescheduleMeeting}
          hosts={hosts}
          meetingTypes={meetingTypes}
          workspaceId={effectiveWorkspaceId}
          currentUser={user}
          isOwner={isOwner}
          currentUserRole={rawUserRole}
          existingMeetings={meetings}
          availabilitySettings={availabilitySettings}
          onClose={() => setRescheduleMeeting(null)}
          onSchedule={handleSaveReschedule}
        />
      )}

      {/* Reschedule Locked Notice Dialog */}
      {rescheduleLockedMeeting && (
        <div className="cal-cancel-popup-overlay" onClick={() => setRescheduleLockedMeeting(null)}>
          <div className="cal-cancel-popup-card" onClick={(e) => e.stopPropagation()}>
            <div className="cal-cancel-popup-header">
              <div
                className="cal-cancel-popup-icon-wrap"
                style={{ background: '#fef3c7', color: '#d97706' }}
              >
                <i className="fas fa-lock"></i>
              </div>
              <div className="cal-cancel-popup-title-wrap">
                <h3 className="cal-cancel-popup-title">Rescheduling Not Allowed</h3>
                <p className="cal-cancel-popup-subtitle">
                  This meeting is already open to join or currently in progress.
                </p>
              </div>
              <button
                type="button"
                className="cal-cancel-popup-close"
                onClick={() => setRescheduleLockedMeeting(null)}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div
              style={{
                padding: '4px 24px 20px',
                fontSize: '13px',
                color: '#475569',
                lineHeight: '1.55',
              }}
            >
              Because the meeting access window is already open (within 15 minutes of start time) or
              in progress, rescheduling is locked to prevent attendee confusion and missed sessions.
              If you cannot attend, you can cancel the meeting or join the call now.
            </div>

            <div className="cal-cancel-popup-footer">
              <button
                type="button"
                className="cal-cancel-popup-btn secondary cal-btn-text"
                onClick={() => setRescheduleLockedMeeting(null)}
              >
                Dismiss
              </button>
              {rescheduleLockedMeeting.conferencingUrl && (
                <a
                  href={rescheduleLockedMeeting.conferencingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cal-cancel-popup-btn danger cal-btn-danger"
                  style={{
                    background: '#2563eb',
                    borderColor: '#2563eb',
                    color: '#fff',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => setRescheduleLockedMeeting(null)}
                >
                  <i className="fas fa-video" style={{ marginRight: '6px' }}></i> Join Meeting Now
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {isScheduleOpen && canCreateMeeting && (
        <ScheduleMeetingModal
          hosts={hosts}
          meetingTypes={meetingTypes}
          defaultDate={scheduleDefaultDate}
          workspaceId={effectiveWorkspaceId}
          currentUser={user}
          isOwner={isOwner}
          currentUserRole={rawUserRole}
          existingMeetings={meetings}
          availabilitySettings={availabilitySettings}
          defaultSelectedLead={scheduleLead}
          onClose={() => {
            setIsScheduleOpen(false);
            setScheduleDefaultDate(null);
            setScheduleLead(null);
          }}
          onSchedule={handleScheduleNewMeeting}
        />
      )}

      {isGroupScheduleOpen && canCreateMeeting && (
        <ScheduleGroupMeetingModal
          hosts={hosts}
          meetingTypes={meetingTypes}
          defaultDate={scheduleDefaultDate}
          workspaceId={effectiveWorkspaceId}
          currentUser={user}
          isOwner={isOwner}
          currentUserRole={rawUserRole}
          existingMeetings={meetings}
          availabilitySettings={availabilitySettings}
          onClose={() => {
            setIsGroupScheduleOpen(false);
            setScheduleDefaultDate(null);
          }}
          onSchedule={handleScheduleNewMeeting}
        />
      )}

      {isSettingsOpen && (
        <MeetingSettingsModal
          settings={availabilitySettings}
          meetingTypes={meetingTypes}
          userEmail={currentUserEmail}
          ownerEmail={ownerEmail}
          workspaceId={effectiveWorkspaceId}
          onClose={() => setIsSettingsOpen(false)}
          onSaveSettings={handleSaveSettings}
        />
      )}
    </div>
  );
}
